import React from 'react';
import DefaultLayout from '../../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../../layouts/TemplateColumn';
import { PageHeader, PageSection, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, TextBlock, DataTable } from '../../../components';
import GlobalSafetyData, { globalSafetyBlock } from './_global_safety_data'
import { StaticImage } from 'gatsby-plugin-image';
import ovarianaeSafetyTable from "../../../assets/safety-AE-incidence-mobile-ovarian.svg";
import ovarianlabSafetyTable from "../../../assets/safety-lab-abnormalities-mob-ovarian.svg";
import { jobCodes } from '../../../util/data/jobCodes';


// SEO INFORMATION 
const pageMeta = {
    title: `KEYNOTE-B96 - Adverse Reactions & Safety Data`,
    keywords: `keywords`,
    description: `Health care professionals may review selected adverse reactions reported in the KEYNOTE-B96 clinical trial in patients with a platinum resistant ovarian cancer.`,
    schemaJsonLD: [
       `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/safety/adverse-reactions/ovarian-cancer-keynote-b96/","@type":"ApprovedIndication","name":"KEYTRUDA and KEYTRUDA QLEX™","alternateName":"pembrolizumab and berahyaluronidase alfa-pmph","description":"KEYTRUDA and KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph) Injection 165 mg + 2,000 units/mL are each indicated, in combination with paclitaxel, with or without bevacizumab, for the treatment of adult patients with platinum-resistant epithelial ovarian, fallopian tube, or primary peritoneal carcinoma whose tumors express PD-L1 (CPS ≥1) as determined by an FDA-authorized test, and who have received 1 or 2 prior systemic treatment regimens."}`,
       `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/safety/adverse-reactions/ovarian-cancer-keynote-b96/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended only for residents of the United States and its territories, including Puerto Rico."}`,
       `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/safety/adverse-reactions/ovarian-cancer-keynote-b96/","@type":"MedicalWebPage","name":"Selected Safety Data From KEYNOTE⁠-⁠B96","description":"The safety of KEYTRUDA in combination with paclitaxel, with or without bevacizumab, was evaluated in 463 patients with epithelial ovarian, fallopian tube, or primary peritoneal carcinoma whose tumors express PD-L1 (CPS ≥1)"}`
    ]
}

//from /src/util/data/indications.js
// UPDATE THIS TO THE CORRECT INDICATION #
const indicationId = 41;
const jobCode = jobCodes[42].jobCode;

const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];

const crossLinkContent = {
    sections : [
        {
            heading: "Related Information",
            items: [
                { text: 'Treatment Monitoring & Management', url: '/safety/monitoring-managing-adverse-reactions/' },
                { text: 'Dose Modifications', url: '/dosing/dose-modifications/' },
                ]
        },
    ],
    interestLinks : {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
};

const anchorLinkModelData = [
    {label: 'Incidence of Selected Immune-Mediated Adverse Reactions'},
    {label: 'Management of Selected Immune-Mediated Adverse Reactions'},
    {label: 'Selected Safety Data From KEYNOTE&#8288;-&#8288;B96'}
]

const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} pageMeta={ pageMeta } location={ location } jobCode={jobCode}>
            <div data-template-name="template-e">
                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader title="Selected Adverse Reactions" isBranding/>
                   <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId}/>
                   </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                        <PageSection bgColor="cloud" title="Severe and Fatal Immune-Mediated Adverse Reactions">
                            <TextBlock>{globalSafetyBlock}</TextBlock>
                            <AnchorLinks items={anchorLinkModelData} />
                        </PageSection>

                        <GlobalSafetyData />

                        <PageSection  title={anchorLinkModelData[2].label}>
                            <TextBlock>
                                <p>The safety of KEYTRUDA in combination with paclitaxel, with or without bevacizumab, was evaluated in 463 patients with epithelial ovarian, fallopian tube, or primary peritoneal carcinoma whose tumors express PD-L1 (CPS ≥1)</p>
                                <p>Patients received KEYTRUDA 400 mg IV every 6 weeks plus paclitaxel 80 mg/m<sup>2</sup> IV with or without bevacizumab 10 mg/kg IV</p>
                                <ul>
                                    <li>Treatment with KEYTRUDA continued until RECIST v1.1-defined progression of disease, unacceptable toxicity, or a maximum of 24 months.</li>
                                </ul>
                                <p><strong>For patients who received KEYTRUDA, the median duration of exposure was 7.4 months (range: 1 day to 35.9 months).</strong></p>
                                <p>Serious adverse reactions occurred in 54% of patients receiving KEYTRUDA and paclitaxel, with or without bevacizumab. Serious adverse reactions in ≥2% of patients were:</p>
                                <ul>
                                    <li>Pneumonia <strong>(4.3%)</strong>, urinary tract infection <strong>(3.9%)</strong>, adrenal insufficiency <strong>(3%)</strong>, hyponatremia <strong>(3%)</strong>, COVID-19 <strong>(2.6%)</strong>, decreased neutrophil count <strong>(2.6%)</strong>, and pulmonary embolism <strong>(2.6%)</strong></li>
                                    <li>Abdominal pain, anemia, colitis, diarrhea, febrile neutropenia, pyrexia, and vomiting: <strong>2.1% each</strong></li>
                                </ul>
                                <p>Fatal adverse reactions occurred in 3.9% of patients receiving KEYTRUDA and paclitaxel, with or without bevacizumab, including:</p>
                                <ul>
                                    <li>Assisted suicide <strong>(0.9%)</strong></li>
                                    <li>Death, intestinal perforation, sepsis, COVID-19, cardio-respiratory arrest, colitis, embolic stroke: <strong>0.4% each</strong></li>
                                </ul>
                                <p>KEYTRUDA was permanently discontinued for adverse reactions in 16% of patients. The most common adverse reactions resulting in permanent discontinuation of KEYTRUDA (≥1%) were:</p>
                                <ul>
                                    <li>Colitis and increased alanine aminotransferase: <strong>1.3% each</strong></li>
                                </ul>
                                <p>Adverse reactions leading to the interruption of KEYTRUDA occurred in 44% of patients. The most common adverse reactions leading to interruption of KEYTRUDA in ≥2% of patients were:</p>
                                <ul>
                                    <li>Urinary tract infection <strong>(3.9%)</strong></li>
                                    <li>Adrenal insufficiency, pyrexia, pneumonitis, and upper respiratory tract infection: <strong>2.6% each</strong></li>
                                    <li>Neutropenia, diarrhea, and COVID-19: <strong>2.1% each</strong></li>
                                </ul>
                            </TextBlock>
                            <DataTable
                                title='Most common (≥20%) adverse reactions for patients treated with KEYTRUDA in combination with paclitaxel with or without bevacizumab'
                                mobileImage={() => <img src={ovarianaeSafetyTable} style={{width:'100%', maxWidth:'500px', margin: '0 auto'}} alt="Most Common (≥20%) Adverse Reactions for Patients Treated With KEYTRUDA® (pembrolizumab) in Combination With paclitaxel With or Without bevacizumab From KEYNOTE-B96"/>}
                                desktopImage={() => <StaticImage quality={90} placeholder="DOMINANT_COLOR" src="../../../assets/safety-AE-incidence-desktop-ovarian.png" alt="Most Common (≥20%) Adverse Reactions for Patients Treated With KEYTRUDA® (pembrolizumab) in Combination With paclitaxel With or Without bevacizumab From KEYNOTE-B96"/>}
                            />
                            <DataTable
                                title='Most common (≥20%) laboratory abnormalities worsening from baseline'
                                mobileImage={() => <img src={ovarianlabSafetyTable} style={{width:'100%', maxWidth:'500px', margin: '0 auto'}} alt="Most Common (≥20%) Laboratory Abnormalities Worsening From Baseline From KEYNOTE-B96"/>}
                                desktopImage={() => <StaticImage quality={90} placeholder="DOMINANT_COLOR" src="../../../assets/safety-lab-abnormalities-desk-ovarian.png" alt="Most Common (≥20%) Laboratory Abnormalities Worsening From Baseline From KEYNOTE-B96"/>}
                            />
                            <TextBlock>
                                <p>For patients treated with KEYTRUDA in combination with paclitaxel and bevacizumab (n=169), decreased white blood cell count <strong>(27%)</strong>, stomatitis <strong>(22%)</strong>, and pyrexia <strong>(21%)</strong> were also reported as adverse reactions.</p>
                            </TextBlock>
                        </PageSection>
                    </TemplateColumn>
                    {/* End Main Column */}
                    
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>

                </TemplateColumnWithSidebarContainer>

                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent}/>
                    </PageSection>
                </TemplateColumn>
            </div>
        </DefaultLayout>
    );
};

export default Page;
